import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StatusBadge } from "@/components/shared/StatusBadge";
import * as Icons from "lucide-react";
import { AllFieldsPanel } from "@/components/explorer/AllFieldsPanel";
import { AccountSuspension } from "@/components/accounts/AccountSuspension";
import { text, fmtTs, inr } from "@/lib/format";
import { preview } from "@/lib/fieldCodec";

export function CustomerDrawer({ c, users, projects, payments, onClose }) {
  const live = c ? users.find((u) => u.id === c.id) || c.raw : null;
  const projs = c ? projects.filter((p) => c.uids.includes(p.customer_id)) : [];
  const pays = c ? payments.filter((p) => c.uids.includes(p.customer_id)) : [];
  const profile = c ? [
    ["Name", c.name], ["Phone", c.phone], ["Email", c.email], ["City", c.city], ["Address", c.address],
    ["Firebase uid", c.uid], ["Joined", fmtTs(c.created_at)], ["Last login", fmtTs(c.last_login_at)],
    ["Auth sign-in", c.auth?.uid ? (c.auth_disabled ? "Disabled" : "Enabled") : "Unknown (service account missing)"],
    ["Linked pro id", c.raw.linked_professional_id], ["Preferred contact", c.raw.preferred_contact_time], ["Profile complete", c.raw.profile_complete == null ? null : String(c.raw.profile_complete)],
  ] : [];
  const missing = profile.filter(([k, v]) => ["Name", "Phone", "Email", "City", "Address"].includes(k) && !v).map(([k]) => k);
  return (
    <Sheet open={!!c} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="cust-drawer">
        {c && (
          <>
            <SheetHeader className="text-left">
              <SheetTitle className="font-heading" data-testid="cust-drawer-name">{c.name || "Unnamed customer"}</SheetTitle>
              <SheetDescription className="font-mono">{c.uid}{c.docIds.length > 1 ? ` · merged ${c.docIds.join(", ")}` : ""}</SheetDescription>
            </SheetHeader>
            {missing.length > 0 && <p className="mt-3 text-[11px] text-amber-800" data-testid="cust-missing">Not captured yet: {missing.join(", ")}</p>}
            <div className="mt-4 grid grid-cols-2 gap-3" data-testid="cust-profile">
              {profile.map(([k, v]) => (
                <div key={k} className="rounded-lg border border-border bg-secondary/50 p-3">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{k}</p>
                  <p className="text-sm text-foreground mt-1 break-words">{v || "—"}</p>
                </div>
              ))}
            </div>
            <AccountSuspension kind="customer" entity={live} uid={c.uid} phone={c.phone} name={c.name || "customer"} />
            <Section icon="ListTree" title={`Every recorded field (${Object.keys(c.raw).length})`}>
              <div className="rounded-lg border border-border divide-y divide-border">
                {Object.keys(c.raw).filter((k) => k !== "id").sort().map((k) => (
                  <div key={k} className="flex justify-between gap-3 px-3 py-1.5 text-xs"><span className="font-mono text-muted-foreground">{k}</span><span className="text-right break-all">{preview(c.raw[k], 80)}</span></div>
                ))}
              </div>
            </Section>
            {c.addrs.length > 0 && <Section icon="MapPin" title={`Saved addresses (${c.addrs.length})`}>{c.addrs.map((a) => <Row key={a.id} left={text(a.formatted_address || a.address || a.label)} sub={`${text(a.city)} ${text(a.pincode, "")}`} />)}</Section>}
            <Section icon="Send" title={`Requests (${c.reqs.length})`}>
              {c.reqs.map((r) => <Row key={r.id} left={text(r.title || r.category)} sub={`${r.lead_code ? `Lead ${r.lead_code} · ` : ""}${fmtTs(r.created_at)}`} right={<StatusBadge status={r.internal_status || r.customer_status || "open"} />} />)}
              {c.reqs.length === 0 && <Empty />}
            </Section>
            <Section icon="FolderKanban" title={`Projects (${projs.length})`}>
              {projs.map((p) => <Row key={p.id} left={text(p.title || p.category)} sub={p.project_number ? `#${p.project_number}` : p.id} right={<StatusBadge status={p.status || "active"} />} />)}
              {projs.length === 0 && <Empty />}
            </Section>
            <Section icon="CreditCard" title={`Payments (${pays.length})`}>
              {pays.map((p) => <Row key={p.id} left={inr(p.amount)} sub={`${text(p.kind)} · ${text(p.provider)}`} right={<StatusBadge status={p.status || "captured"} />} />)}
              {pays.length === 0 && <Empty />}
            </Section>
            <AllFieldsPanel path="users" id={c.id} extraDocs={c.docIds.filter((d) => d !== c.id).map((d) => ({ label: "Merged duplicate", path: "users", id: d }))} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ icon, title, children }) {
  const Icon = Icons[icon] || Icons.Circle;
  return (
    <div className="mt-6">
      <h4 className="font-heading font-semibold text-foreground mb-2 flex items-center gap-2 text-sm"><Icon className="h-4 w-4 text-primary" /> {title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
const Row = ({ left, sub, right }) => (
  <div className="flex items-center justify-between rounded-lg border border-border p-3">
    <div className="min-w-0"><p className="text-sm font-medium text-foreground truncate">{left}</p><p className="text-[11px] font-mono text-muted-foreground truncate">{sub}</p></div>
    {right && <div className="shrink-0">{right}</div>}
  </div>
);
const Empty = () => <p className="text-xs text-muted-foreground py-3 text-center">None.</p>;
